import { Container, FormControl, FormLabel, Heading, Input, InputGroup, InputLeftAddon, Switch } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CardForm from 'components/ui/CardForm';
import FixedBar from 'components/ui/FixedBar';
import FormControlGroup from 'components/ui/FormControlGroup';
import SubmitButton from 'components/ui/SubmitButton';

import { ResourceCreatableSelect, SelectOption } from 'components/ui/ResourceCreatableSelect';
import { useUsers } from 'features/users/hooks/useUsers';
import { useMessages } from 'hooks/useMessages';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useCompaniesContext } from '../contexts/CompaniesContext';
import { useCompanyCategories } from '../hooks/useCompanyCategories';
import { useSaveCompany } from '../hooks/useSaveCompany';
import ControlledSelect from './ControlledSelect';
import UploadAsset from './UploadAsset';

export interface FormValues {
  name: string;
  website: string;
  logo?: string;
  category: SelectOption;
  customerServiceUser: SelectOption;
  active?: boolean;
}

interface FormCompanyProps {
  company?: any,
  onNext?: any
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required(),
  website: Yup.string().required(),
  category: Yup.mixed().required(),
  customerServiceUser: Yup.mixed().required(),
});

export default function FormCompany({ onNext, company }: FormCompanyProps) {
  const { setCompanyId } = useCompaniesContext();
  const isEditing: boolean = !!company;

  const { data: companyCategories = [], isFetched } = useCompanyCategories();
  const { data: users = []  } = useUsers();

  const categoryOptions: SelectOption[] = companyCategories.map(({ id: value, name: label}) => ({ value, label }));
  const userOptions: SelectOption[] = users.map(({ id: value, name: label}) => ({ value, label }));

  const defaultValues: FormValues = {
    name: company?.name,
    website: company?.website,
    logo: company?.logo,
    category: company?.category ? {
      value: company.category.id,
      label: company.category.name
    }: null,
    customerServiceUser: company?.customerServiceUser ? {
      value: company.customerServiceUser.id,
      label: company.customerServiceUser.name
    }: null,
    active: company?.active,
  };

  const { control, register, handleSubmit, reset, watch, setValue, formState: { errors, isValid } } = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues
  });

  const { onError, onSuccess } = useMessages( { success: 'Dados atualizados com sucesso!' } );

  const [isActive, category, logo] = watch(['active', 'category', 'logo']);

  const { 
    onSaveCompany, 
    isPending, 
  } = useSaveCompany({ 
        companyId: company?.id,
        onSuccess: ({ data: { company } }) => {

          if (!isEditing) {
            setCompanyId(company.id);
            reset();
            onNext();
          }
          else {
            onSuccess();
          }
          //navigate('/clientes');
          
          // if (!company) {
          //   openSuccessDialog({
          //     title: 'Novo usuário cadastrado!',
          //     description: `Oriente o usuário ${user.name} a aceitar o convite enviado para ${user.email}`,
          //     actionButtonText: 'Criar mais um usuário',
          //     actionButton: '/usuarios/novo',
          //     closeButtonText: 'Tudo certo'
          //   });
          // }
        },
        onError: (error) => onError(error?.response?.data?.message)
      });
      
  const onSubmit: SubmitHandler<FormValues> = ({ name, website, logo, category, customerServiceUser, active }) => onSaveCompany({
    name, 
    website, 
    logo, 
    categoryId: category?.value ?? '', 
    customerServiceUserId: customerServiceUser?.value ?? '', 
    active
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Container 
        variant="internal"
        flexDirection="column" 
        mb="180px"
      >
        <Heading as="h1" size="h1">
          Dados da empresa
          <span>Como a empresa será identificada no sistema</span>
        </Heading>

        <CardForm>
          <FormControl isInvalid={!!errors.name} isRequired>
            <FormLabel>Nome</FormLabel>
            <Input {...register('name')} />
          </FormControl>

          <FormControlGroup>
            <FormControl isInvalid={!!errors.website} isRequired>
              <FormLabel>Site da empresa</FormLabel>
              <InputGroup>
                <InputLeftAddon>https://</InputLeftAddon>
                <Input {...register('website')} />
              </InputGroup>
              
            </FormControl>
          { isFetched && (
            <FormControl isInvalid={!!errors.category} isRequired>
              <FormLabel>Categoria</FormLabel>
              <ResourceCreatableSelect
                control={control}
                name="category"
                resourceKey="companyCategories"
                listPath="/company-categories"
                createPath="/company-categories"
                placeholder="Selecione uma opção"
              />
            </FormControl>
          )}

            <ControlledSelect
              control={control}
              name="customerServiceUser"
              options={userOptions}
              placeholder="Selecione uma opção"
              label="Atendimento"
              isRequired
            />
          
            {/* <FormControl isInvalid={!!errors.customerServiceUserId}>
              <FormLabel>Atendimento</FormLabel>
              <Select 
                ref={ref}
                name="customerServiceUserId"
                placeholder="Selecione uma opção"
                onChange={(item) => setValue('customerServiceUserId', item?.value, { shouldValidate: true })}
                //onBlur={(e) => setValue('customerServiceUserId', e.target.value, { shouldValidate: true })}
                onBlur={(e) => {
                  if (!customerServiceUserId) setValue('customerServiceUserId', customerServiceUserId, { shouldValidate: true })
                }}
                options={users.map(({ id: value, name: label}) => ({ value, label }))} 
              />
            </FormControl> */}
          </FormControlGroup>

          <FormControl>
            <FormLabel>Marca da empresa</FormLabel>
            <UploadAsset
              defaultValue={defaultValues.logo}
              onChange={url => setValue('logo', url, { shouldValidate: true })}
            />
          </FormControl>  

        { isEditing && (
          <FormControl isInvalid={!!errors.active}>
            <FormLabel>Status</FormLabel>
            <Switch {...register('active')}>
            { isActive ? 'Ativo' : 'Inativo' }
            </Switch>
          </FormControl>
        )}
        </CardForm>
      </Container>
      
      <FixedBar>
        <SubmitButton 
          ml="auto"
          isDisabled={isPending || !isValid}
          isLoading={isPending}
          hideIcon={isEditing}
        >
          {isEditing ? 'Salvar' : 'Continuar' }
        </SubmitButton>
      </FixedBar>
    </form>
  )
}